<template>
	<div class="title-detail-hero-details" id="title-detail-hero-details" v-if="!isEmpty">
		<HiddenHorizontalScrollbar>
			<template v-if="showTextRecommendationTools">
				<div class="title-detail-hero-details__item">
					<span class="title-detail-hero-details__item__action" @click="openTextRecommendationModal">
						{{ $t('WEBAPP_RECOMMEND') }}
					</span>
				</div>
			</template>

			<template v-if="streamingChartRank">
				<div class="title-detail-hero-details__item">
					<img class="jw-logo" src="appassets/img/jw-icon.png" alt="JustWatch Logo" />
					<span class="title-detail-hero-details__item__rank">{{ streamingChartRank }}</span>
					<div
						v-if="showArrow"
						class="arrow-container"
						:class="{
							'arrow-container--up': streamingChartInfo?.trend === 'UP',
							'arrow-container--stable': streamingChartInfo?.trend === 'STABLE',
							'arrow-container--down': streamingChartInfo?.trend === 'DOWN',
						}"
					>
						<FontAwesomeIcon :icon="faCaretUp" :class="arrowClasses" size="lg" />
						<p>{{ trendDifference }}</p>
					</div>
				</div>
			</template>
			<template v-if="jwScore">
				<div class="title-detail-hero-details__item">
					<img class="jw-star" src="appassets/img/jw-icon.png" alt="JustWatch Icon" />
					<span>{{ jwScore }}%</span>
				</div>
			</template>
			<template v-if="imdbScore">
				<div class="title-detail-hero-details__item" @click="onRatingClick($event)">
					<img class="imdb-logo" src="appassets/img/imdb-logo.png" alt="ImDB Logo" />
					<span class="imdb-score">{{ imdbScore }} {{ formatVotes(imdbVoteCount) }}</span>
				</div>
			</template>

			<template v-if="ageRating">
				<div class="title-detail-hero-details__item">
					{{ ageRating }}
					<span class="title-detail-hero-details__item--icon">
						<FontAwesomeIcon :icon="faInfoCircle" />
					</span>
					<div class="popover">
						<p>{{ t('WEBAPP_AGERATING') }}</p>
					</div>
				</div>
			</template>
			<div class="title-detail-hero-details__item" v-if="runtime">{{ runtime }}</div>
		</HiddenHorizontalScrollbar>

		<TextRecommendationModal
			v-if="displayTextRecommendationModal"
			:title="title"
			@close="closeTextRecommendationModal"
			@success="closeTextRecommendationModal"
		/>
	</div>
</template>

<script setup lang="ts">
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { computed, onMounted, ref } from 'vue';
import { formatRuntime } from '@/helpers/title-helper';
import { ModuleContentTitleTrend } from '@/@types/graphql-types';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { numberToCompactString } from '@/helpers/NumberFormat';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import TextRecommendationModal from '@/features/why-to-watch/components/TextRecommendationModal.vue';
import { faCaretUp, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

// SEASON_PAGE_BUYBOX_EXP
import { disableScroll, enableScroll } from '@/helpers/scroll-helper';
import { preventNonSignedInAction } from '@/helpers/prevent-non-signed-in-action-helper';
import { AccountModes } from '@/enums/login';
// SEASON_PAGE_BUYBOX_EXP

import PromotionsConfig from '@/features/promotions/config.json';
import { useWtwTracking } from '@/features/why-to-watch/tracking';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

const { t } = useI18n();

const props = defineProps<{
	title: TitleDetail;
	showChartInfo: boolean;
}>();

const { isAdmin, isEditor, profileIsComplete, profileInfo, isLoggedIn } = storeToRefs(useUserStore());
const { country } = storeToRefs(useLanguageStore());

const route = useRoute();

const isMounted = ref(false);
onMounted(() => {
	isMounted.value = true;
});

const displayTextRecommendationModal = ref<boolean>(false);

const textRecommendationUserCountries = PromotionsConfig.w2w.pro_users_countries;

const isEmpty = computed(() => {
	return !streamingChartRank.value && !jwScore.value && !imdbScore.value && !ageRating.value && !runtime.value;
});

const streamingChartInfo = computed<any>(() => {
	return props.title.streamingCharts?.edges?.[0].streamingChartInfo;
});

const streamingChartRank = computed(() => {
	return props.showChartInfo && streamingChartInfo.value?.rank
		? `${streamingChartInfo.value?.rank.toString().padStart(2, '0')}.`
		: null;
});

const ageRating = computed(() => {
	return props.title.__typename === 'Season'
		? props.title.show.content.ageCertification
		: props.title.content.ageCertification;
});

const runtime = computed(() => {
	if (props.title?.content?.runtime) {
		const runtimeValue = props.title.content.runtime!;
		const { translationKey, minutes, hours } = formatRuntime(runtimeValue);
		return t(translationKey, { minutes, hours }) as string;
	}
	return null;
});

const imdbScore = computed(() => {
	return props.title.content.scoring.imdbScore?.toFixed(1);
});

const imdbVoteCount = computed(() => {
	return props.title.content.scoring.imdbVotes;
});

// eslint-disable-next-line vue/return-in-computed-property
const trendDifference = computed(() => {
	if (streamingChartInfo.value?.trend === ModuleContentTitleTrend.Up) {
		return `+${streamingChartInfo.value?.trendDifference}`;
	}
	if (streamingChartInfo.value?.trend === ModuleContentTitleTrend.Stable) {
		return '—';
	}
	if (streamingChartInfo.value?.trend === ModuleContentTitleTrend.Down) {
		return `${streamingChartInfo.value?.trendDifference}`;
	}
});

const showArrow = computed(() => {
	return streamingChartInfo.value?.trend;
});

const arrowClasses = computed(() => {
	if (!showArrow.value) {
		return null;
	}
	return {
		'arrow-container__arrow': !!streamingChartInfo.value?.trend,
		'arrow-container__arrow--up': streamingChartInfo.value?.trend === ModuleContentTitleTrend.Up,
		'arrow-container__arrow--stable': streamingChartInfo.value?.trend === ModuleContentTitleTrend.Stable,
		'arrow-container__arrow--down': streamingChartInfo.value?.trend === ModuleContentTitleTrend.Down,
	};
});

const jwScore = computed(() => {
	return Math.round((props.title.content.scoring?.jwRating || 0) * 100);
});

const showTextRecommendationTools = computed(() => {
	return false;
	if (!isLoggedIn.value) return false;

	if (isAdmin.value || isEditor.value) {
		return true;
	} else if (!textRecommendationUserCountries.includes(country.value)) {
		return false;
	}

	const profileCountry = profileInfo.value?.country;
	return profileIsComplete.value ? isLoggedIn.value && profileCountry === country.value : isLoggedIn.value;
});

const { trackWtw } = useWtwTracking({ title: () => props.title });

function openTextRecommendationModal() {
	if (preventNonSignedInAction(AccountModes.REGISTRATION_OPTIONS)) return;

	trackWtw('modalOpened');

	displayTextRecommendationModal.value = true;
	disableScroll();
}

function closeTextRecommendationModal() {
	displayTextRecommendationModal.value = false;
	enableScroll();
}

function onRatingClick($event: Event) {
	$event.preventDefault();
	$event.stopPropagation();
	$event.stopImmediatePropagation();

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'rating_click',
			label: 'IMDB',
		},
		[]
	);

	const imdbId = props.title.content.externalIds.imdbId;

	// Reconstruct IMDb link to use current IMDb website URL formatting
	if (route.name === 'app.detail.show.season') {
		let idInPath = imdbId;
		if (imdbId?.includes(':')) {
			const [id] = imdbId.split(':', 2);
			idInPath = id;
		}
		window.open(`https://www.imdb.com/title/${idInPath}?ref_=ref_ext_justwatch`);
	} else {
		window.open(`https://www.imdb.com/title/${imdbId}?ref_=ref_ext_justwatch`);
	}
}

function formatVotes(votes: number | null | undefined) {
	if (!votes || votes < 30) return '';
	if (votes >= 10000) return `(${numberToCompactString(votes, 0)})`;
	return `(${numberToCompactString(votes, 1)})`;
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
@import '~@/styles/rtl-support-mixins';
@import '~@/styles/mixins.scss';

.title-detail-hero-details {
	display: flex;
	align-items: center;
	overflow: visible;
	flex-wrap: wrap;
	width: 100%;

	.jw-logo {
		height: 16px;
		width: 16px;
	}

	&__item {
		display: flex;
		align-items: center;
		position: relative;

		.popover {
			position: absolute;
			display: none;
			height: fit-content;
			width: fit-content;
			padding: 8px 8px;
			background-color: #2a3644;
			z-index: 11;
			white-space: nowrap;
			left: calc(100% - 38px);
			transform: translate(-100%);
			line-height: 18px;
			border-radius: 8px;

			p {
				text-transform: initial;
				margin: 0;
				font-size: 14px;
				font-weight: 400;
				color: var(--jw-text-medium);
			}
		}

		&--icon {
			font-size: 13px;
			padding-bottom: 10px;
			padding-inline-start: 5px;
			color: #727f92;

			&:hover {
				cursor: pointer;

				& + .popover {
					display: block;
				}
			}
		}

		&__icon {
			display: flex;
			justify-content: center;
			align-items: center;
			width: 24px;
			height: 24px;
			margin-inline-end: 5px;
		}

		&__action {
			color: var(--link-link-medium, #78a6b8);
			font-size: 16px;
			font-style: normal;
			font-weight: 400;
			line-height: 16px;
			letter-spacing: 0.24px;
			margin-inline-start: 5px;

			@media (min-width: 1051px) {
				display: none;
			}
		}

		&__rank {
			margin: 0 8px;
			font-size: 16px;
			font-weight: 900;
			color: #fbc500;
		}

		.arrow-container {
			margin-inline-end: auto;
			display: flex;
			justify-content: center;
			align-items: center;
			border: 1px solid;
			border-radius: 16px;
			padding: 2px 5px;
			width: fit-content;

			p {
				font-size: 12px;
				margin: 0;
			}

			svg {
				margin-inline-end: 4px;
				font-size: 14px;
			}

			&--up {
				color: $jw-color-green;
				border-color: $jw-color-green;

				p {
					color: $jw-color-green;
				}
			}

			&--stable {
				color: #797979;
				border-color: #797979;

				p {
					color: #797979;
					font-weight: 900;
					padding: 0 8px;
				}
			}

			&--down {
				color: $jw-color-red;
				border-color: $jw-color-red;

				p {
					color: $jw-color-red;
				}
			}

			&__arrow {
				font-size: 24px;
				margin-inline-end: 4px;

				&--up {
					color: $jw-color-green;
				}

				&--stable {
					color: #797979;
					transform: rotate(90deg);
					display: none;
				}

				&--down {
					color: $jw-color-red;
					transform: rotate(180deg);
				}
			}
		}

		.imdb-score {
			color: #78a6b8;

			&:hover {
				cursor: pointer;
				color: #9cb2bc;
			}
		}

		.recommend {
			color: #fbc500;
		}
	}

	.jw-star {
		height: 16px;
		width: 16px;
		margin-inline-end: 4px;
	}

	.imdb-logo {
		width: 24px;
		margin-inline-end: 4px;
	}

	.tomato-meter-logo {
		width: 16px;
		margin-inline-end: 3px;
		vertical-align: sub;
		margin-bottom: 2px;
	}
}
.title-detail-hero-details {
	.title-detail-hero-details__item {
		&:not(:last-child)::after {
			content: '-';
			margin-inline: 8px;
		}
	}
}
</style>
